import React, { useRef, useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useOutletContext,
  Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, getUserRole } from './firebase';
import { AuthContext, useAuth } from './authContext';
import useSessionTimeout from './hooks/useSessionTimeout';
import {
  ensureSessionStart,
  checkSessionExpiry,
  touchActivity,
  clearSession,
  startSession,
  consumeLoginAttempt,
  clearLoginAttempt,
} from './utils/sessionTimeout';

import Sidebar from './components/Sidebar';
import VersionChecker from './components/VersionChecker';
import Header from './components/Header';
import LoginForm from './components/LoginForm';
import RouteFallback from './components/RouteFallback';
import AwaitingApproval from './pages/AwaitingApproval';
import AccountRetired from './pages/AccountRetired';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { GSTDisplayProvider } from './contexts/GSTDisplayContext';

// Every page stylesheet, eagerly and in a fixed order. Page components below are
// lazy(), so their CSS would otherwise land in async chunks and be injected in
// navigation order — which would break pages that collide on global selectors.
// See the file header for the full explanation. Must stay above the lazy imports.
import './styles/pageStyles';

// Home is the landing route and only 16 lines — eager, so the first paint after
// login needs no extra round trip. Profile is not routed (it renders inside the
// chrome below), so it stays eager too. Everything else is split per module.
import Home from './pages/Home';
import Profile from './pages/Profile/Profile';

// Route-level code splitting. The webpackChunkName comments group co-located
// routes into one chunk so that e.g. /inventory -> /inventory/costing does not
// trigger a second download. The route wrapper components further down stay
// eager on purpose: they run the permission check, so a user with No Access is
// redirected home without ever fetching the module's chunk.
const SalesInvoicesList = lazy(() => import(/* webpackChunkName: "sales-invoices" */ './pages/SalesInvoices/SalesInvoicesList'));
const SalesInvoiceProject = lazy(() => import(/* webpackChunkName: "sales-invoices" */ './pages/SalesInvoices/SalesInvoiceProject'));
const SalesAnalyticsPage = lazy(() => import(/* webpackChunkName: "sales-invoices" */ './pages/SalesInvoices/SalesAnalyticsPage'));
// DEPRECATED: InvoiceEditor/ (the standalone full-page invoice editor) is no longer
// routed — invoices open in the shared InvoiceModal from InvoicesTab.jsx instead.
// The files stay on disk; QuotationEditorPage still reuses InvoiceEditorPage.css.
const QuotationEditorPage = lazy(() => import(/* webpackChunkName: "sales-invoices" */ './pages/SalesInvoices/QuotationEditor/QuotationEditorPage'));
// DEPRECATED: standalone Quotation module is merged into Quotation & Invoices
// (/sales-invoices); the /quotation* routes are Navigate redirects. QuotationList,
// QuotationQuickView and CreateQuotation are no longer imported here — they were
// bundled but never rendered. The files stay on disk for reuse inside the new
// editor, and their stylesheets stay pinned in styles/pageStyles.js.
const AccessPermissions = lazy(() => import(/* webpackChunkName: "access-permissions" */ './pages/AccessPermissions'));
const ProjectCostingDashboard = lazy(() => import(/* webpackChunkName: "project-costing" */ './pages/ProjectCosting/ProjectCostingDashboard'));
const ProjectCostingDetail = lazy(() => import(/* webpackChunkName: "project-costing" */ './pages/ProjectCosting/ProjectCostingDetail'));
const Claims = lazy(() => import(/* webpackChunkName: "claims" */ './pages/Claims/Claims'));
const ProjectsList = lazy(() => import(/* webpackChunkName: "projects" */ './pages/Projects/ProjectsList'));
const AllProjectsView = lazy(() => import(/* webpackChunkName: "projects" */ './pages/Projects/AllProjectsView'));
const TeamManagement = lazy(() => import(/* webpackChunkName: "team-management" */ './pages/TeamManagement/TeamManagement'));
const DailyTransport = lazy(() => import(/* webpackChunkName: "daily-transport" */ './pages/DailyTransport/DailyTransport'));
const MaterialCatalogue = lazy(() => import(/* webpackChunkName: "material-catalogue" */ './pages/MaterialCatalogue/MaterialCatalogue'));
const MaterialCategory = lazy(() => import(/* webpackChunkName: "material-catalogue" */ './pages/MaterialCatalogue/MaterialCategory'));
const MaterialProduct = lazy(() => import(/* webpackChunkName: "material-catalogue" */ './pages/MaterialCatalogue/MaterialProduct'));
// The redesigned product page, running alongside the classic one so the two can
// be compared on real data. Same chunk — a user toggling between them should not
// wait for a second download.
const MaterialProductV2 = lazy(() => import(/* webpackChunkName: "material-catalogue" */ './pages/MaterialCatalogue/v2/MaterialProductV2'));
const WorksCatalogue = lazy(() => import(/* webpackChunkName: "works-catalogue" */ './pages/WorksCatalogue/WorksCatalogue'));
const WorksCatalogueProject = lazy(() => import(/* webpackChunkName: "works-catalogue" */ './pages/WorksCatalogue/WorksCatalogueProject'));
const SupplierList = lazy(() => import(/* webpackChunkName: "supplier-list" */ './pages/SupplierList/SupplierList'));
const SupplierContacts = lazy(() => import(/* webpackChunkName: "supplier-list" */ './pages/SupplierList/SupplierContacts'));
const DefectTracker = lazy(() => import(/* webpackChunkName: "defect-tracker" */ './pages/DefectTracker/DefectTracker'));
const FollowUps = lazy(() => import(/* webpackChunkName: "follow-ups" */ './pages/FollowUps/FollowUps'));
const FollowUpProjectPage = lazy(() => import(/* webpackChunkName: "follow-ups" */ './pages/FollowUps/FollowUpProjectPage'));
const FollowUpListPage = lazy(() => import(/* webpackChunkName: "follow-ups" */ './pages/FollowUps/FollowUpListPage'));
const Notes = lazy(() => import(/* webpackChunkName: "notes" */ './pages/Notes/Notes'));
const PurchaseOrdersPage = lazy(() => import(/* webpackChunkName: "purchasing" */ './pages/Purchasing/PurchaseOrdersPage'));
const Inventory = lazy(() => import(/* webpackChunkName: "inventory" */ './pages/Inventory/Inventory'));
const FactoryStockCostingPage = lazy(() => import(/* webpackChunkName: "inventory" */ './pages/Inventory/FactoryStockCostingPage'));
const InventoryProjectDetail = lazy(() => import(/* webpackChunkName: "inventory" */ './pages/Inventory/InventoryProjectDetail'));
const MovementHistory = lazy(() => import(/* webpackChunkName: "inventory" */ './pages/Inventory/MovementHistory'));
const Attendance = lazy(() => import(/* webpackChunkName: "attendance" */ './pages/Attendance/Attendance'));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard/Dashboard'));
const SOP = lazy(() => import(/* webpackChunkName: "sop" */ './pages/SOP/SOP'));
const SOPDetailPage = lazy(() => import(/* webpackChunkName: "sop" */ './pages/SOP/SOPDetailPage'));
const SystemFurnitureCatalogue = lazy(() => import(/* webpackChunkName: "system-furniture" */ './pages/SystemFurnitureCatalogue/SystemFurnitureCatalogue'));
const GeneralExpenses = lazy(() => import(/* webpackChunkName: "general-expenses" */ './pages/GeneralExpenses/GeneralExpenses'));

const SALES_ROLES = ['bosscontrol', 'boss', 'admin', 'accountant'];
const SUPPLIER_ROLES = ['admin', 'boss', 'bosscontrol'];

// Modules that only support No Access (0) or Full Access (2). Mirrors
// BINARY_ONLY_FEATURES in src/utils/roleHierarchy.js and BINARY_ONLY_MODULES
// in AccessPermissions.jsx — keep all three in sync.
const BINARY_ONLY_MODULES_APP = new Set([
  'home', 'dashboard', 'sales_invoices', 'attendance', 'access_permissions',
  'inventory',
]);

// Derives 0/1/2 access level for a catalogue module from Firestore user data.
// 0 = no access, 1 = limited (read-only, no pricing), 2 = full (edit + pricing).
// Binary-only modules collapse the limited tier — they return 0 or 2 only.
function deriveCatalogueAccessLevel(moduleId, customPermissions, moneyPermissions, role) {
  // Boss Control always has full access to everything, regardless of stored values.
  if (role === 'bosscontrol') return 2;

  const FULL_ROLES = ['bosscontrol', 'boss', 'admin'];
  const isBinary = BINARY_ONLY_MODULES_APP.has(moduleId);

  // ── Access & Permissions has stricter defaults than other binary modules:
  // only Boss Control / Boss / Admin / Accountant are eligible at all. Worker,
  // Team Leader, and Project Manager are HARD-DENIED regardless of any flags
  // that may exist in their stored customPermissions.
  if (moduleId === 'access_permissions') {
    if (role === 'worker' || role === 'team_leader' || role === 'project_manager') {
      return 0;
    }
    if (Array.isArray(customPermissions)) {
      const hasFlag = customPermissions.includes(moduleId);
      const hasMoney = !!(moneyPermissions && moneyPermissions[moduleId]);
      return (hasFlag && hasMoney) ? 2 : 0;
    }
    return role === 'boss' ? 2 : 0;
  }

  // ── Attendance is a desktop-only admin module: hard-deny Worker / Team Leader /
  // Project Manager (they use the mobile app); only Admin / Accountant / Boss /
  // Boss Control are eligible. Respect stored customPermissions when present.
  if (moduleId === 'attendance') {
    if (role === 'worker' || role === 'team_leader' || role === 'project_manager') {
      return 0;
    }
    if (Array.isArray(customPermissions)) {
      return customPermissions.includes('attendance') ? 2 : 0;
    }
    return ['bosscontrol', 'boss', 'admin', 'accountant'].includes(role) ? 2 : 0;
  }

  // ── Quotation & Invoice is binary and restricted to the sales-eligible roles.
  // Its role fallback has to name `accountant` explicitly: the generic FULL_ROLES
  // list below excludes accountant, and falling through would deny a module their
  // own preset grants (DEFAULT_ROLE_PERMISSIONS.accountant in AccessPermissions.jsx).
  if (moduleId === 'sales_invoices') {
    if (Array.isArray(customPermissions)) {
      return customPermissions.includes('sales_invoices') ? 2 : 0;
    }
    return SALES_ROLES.includes(role) ? 2 : 0;
  }

  // ── Claims (Staff Claims) has its own default-role set, distinct from the
  // generic FULL_ROLES list: Boss / Accountant / Admin default to Full (view +
  // edit all claims) while Worker / Team Leader / Project Manager default to
  // Limited (own claims only) — everyone can submit their own expense claims
  // unless explicitly locked out via stored customPermissions. Approve/reject
  // and delete-others'-claims remain separate, narrower role checks inside
  // Claims.jsx and are NOT governed by this access level.
  if (moduleId === 'claims') {
    if (Array.isArray(customPermissions)) {
      if (!customPermissions.includes('claims')) return 0;
      return (moneyPermissions && moneyPermissions.claims) ? 2 : 1;
    }
    if (['boss', 'accountant', 'admin'].includes(role)) return 2;
    return 1; // worker / team_leader / project_manager default to Limited
  }

  // ── General Expenses is an office/finance module: the pool is company-wide
  // overhead, not project data, so Worker / Team Leader / Project Manager get
  // nothing by default rather than the generic Limited fallback below. Stored
  // customPermissions always win, so any of them can still be granted access
  // explicitly. Full (2) = create/edit/reverse, Limited (1) = view only.
  if (moduleId === 'general_expenses') {
    if (role === 'worker' || role === 'team_leader' || role === 'project_manager') {
      return Array.isArray(customPermissions) && customPermissions.includes('general_expenses')
        ? ((moneyPermissions && moneyPermissions.general_expenses) ? 2 : 1)
        : 0;
    }
    if (Array.isArray(customPermissions)) {
      // Absent from a STORED array means No Access, for every role — see the
      // note on the generic path below.
      if (!customPermissions.includes('general_expenses')) return 0;
      return (moneyPermissions && moneyPermissions.general_expenses) ? 2 : 1;
    }
    return ['boss', 'admin', 'accountant'].includes(role) ? 2 : 0;
  }

  if (Array.isArray(customPermissions)) {
    // ── A STORED ARRAY IS AUTHORITATIVE ──────────────────────────────────────
    // Absence of a module means No Access (0) for every role but Boss Control,
    // which returned above. There used to be a "stale array" fallback here
    // granting Boss/Admin Limited instead, on the theory that an array predating
    // a module shouldn't hard-deny it. In practice it meant setting a user to
    // No Access in Access & Permissions still left them able to VIEW the module
    // — the exact bug this replaced — and it disagreed with firestore.rules,
    // which has never had such a fallback and is the real boundary.
    //
    // The accepted cost: when a NEW module is added, users with a stored array
    // get No Access until it is granted in Access & Permissions. That is the
    // correct default for a permission system, and it is visible/fixable there.
    if (!customPermissions.includes(moduleId)) return 0;
    if (isBinary) return 2;
    return (moneyPermissions && moneyPermissions[moduleId]) ? 2 : 1;
  }
  // Role-based fallback when no customPermissions stored in Firestore yet
  if (FULL_ROLES.includes(role)) return 2;
  if (isBinary) return 0;
  return 1; // non-binary modules default to limited access for other roles
}

/* Every feature id the app gates, in Access & Permissions order.
   useCatalogueAccess fails CLOSED, so a module missing from this list resolves
   to No Access for everyone — add new features here as they are built, and keep
   it aligned with ALL_FUNCTIONS in src/pages/AccessPermissions.jsx. */
const GATED_FEATURE_IDS = [
  'material_catalogue', 'works_catalogue', 'sop', 'project_costing',
  'daily_transport', 'defect_tracker', 'follow_ups', 'inventory', 'attendance',
  'team_management', 'projects', 'supplier_list', 'access_permissions',
  'project_follow_ups', 'notes', 'claims', 'general_expenses', 'sales_invoices',
];

/** The full 0/1/2 map for one user. One builder, so the two call sites below
    (normal load and the sessionStorage error path) can never drift apart. */
function buildCatalogueAccessLevels(customPermissions, moneyPermissions, role) {
  const out = {};
  GATED_FEATURE_IDS.forEach((id) => {
    out[id] = deriveCatalogueAccessLevel(id, customPermissions, moneyPermissions, role);
  });
  return out;
}

function RoleRoute({ allow, component: Comp }) {
  const { role } = useAuth();
  if (!allow.includes(role)) return <Navigate to="/" replace />;
  return <Comp role={role} />;
}

// Gates a route on a specific feature access level (0/1/2). Useful for routes
// like /access-permissions where eligibility is decided per-user, not per-role.
function FeatureRoute({ feature, minLevel = 2, component: Comp }) {
  const { role, catalogueAccessLevels } = useAuth();
  const level = catalogueAccessLevels?.[feature] ?? 0;
  if (level < minLevel) return <Navigate to="/" replace />;
  return <Comp role={role} />;
}

// Gates a route on a feature's access level AND (optionally) an eligible-role
// list, passing `role` down the way RoleRoute does. Replaces the old PassRole
// wrapper, which passed the role through with no permission check at all.
//
// Exists because several modules were mounted with a role check only, so a user
// set to No Access in Access & Permissions still reached the page — Quotation &
// Invoice most visibly, which had no permission check of any kind. Level 0 means
// the module is switched off for this user: send them home rather than render a
// page they should not be able to see at all.
function GatedRoleRoute({ feature, allow, component: Comp }) {
  const { role } = useOutletContext();
  const accessLevel = useCatalogueAccess(feature);
  if (allow && !allow.includes(role)) return <Navigate to="/" replace />;
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <Comp role={role} />;
}

function SOPPage() {
  const { role, email } = useOutletContext();
  const accessLevel = useCatalogueAccess('sop');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <SOP role={role} accessLevel={accessLevel} currentUserEmail={email} />;
}

function SOPDetailRoute() {
  const { email, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('sop');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <SOPDetailPage accessLevel={accessLevel} currentUserEmail={email} currentUserName={name} />;
}

function DailyTransportPage() {
  const accessLevel = useCatalogueAccess('daily_transport');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <DailyTransport accessLevel={accessLevel} />;
}

function GeneralExpensesPage() {
  const accessLevel = useCatalogueAccess('general_expenses');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <GeneralExpenses accessLevel={accessLevel} />;
}

// Fails CLOSED: a module missing from the derived map is No Access, not Full.
// Every module the app gates must therefore appear in setCatalogueAccessLevels
// below — an omission now hides a page rather than silently granting it to
// everyone (which is how `sales_invoices` went ungated for so long).
function useCatalogueAccess(moduleId) {
  const { catalogueAccessLevels } = useOutletContext();
  return catalogueAccessLevels?.[moduleId] ?? 0;
}

function HomePage() {
  const { role } = useOutletContext();
  return <Home role={role} />;
}

function ProjectCostingPage() {
  const { role } = useOutletContext();
  const accessLevel = useCatalogueAccess('project_costing');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <ProjectCostingDashboard role={role} />;
}

function ProjectCostingDetailPage() {
  const { role } = useOutletContext();
  const accessLevel = useCatalogueAccess('project_costing');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <ProjectCostingDetail role={role} />;
}

function DefectTrackerPage() {
  const { role, userId } = useOutletContext();
  const accessLevel = useCatalogueAccess('defect_tracker');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <DefectTracker role={role} userId={userId} accessLevel={accessLevel} />;
}

function FollowUpsPage() {
  const { role, userId } = useOutletContext();
  const accessLevel = useCatalogueAccess('follow_ups');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <FollowUps role={role} userId={userId} accessLevel={accessLevel} />;
}

function FollowUpsNewPage() {
  const { role, userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('project_follow_ups');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <FollowUps role={role} userId={userId} userName={name} accessLevel={accessLevel} />;
}

function FollowUpsProjectDetailPage() {
  const { role, userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('project_follow_ups');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <FollowUpProjectPage role={role} userId={userId} userName={name} accessLevel={accessLevel} />;
}

function FollowUpsListDetailPage() {
  const { role, userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('project_follow_ups');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <FollowUpListPage role={role} userId={userId} userName={name} accessLevel={accessLevel} />;
}

// Purchasing (formerly Follow Ups) — reuses the `follow_ups` access key so
// existing user permissions carry over without migration.
function PurchasingPage() {
  const accessLevel = useCatalogueAccess('follow_ups');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <PurchaseOrdersPage accessLevel={accessLevel} />;
}

// Inventory — permission key renamed from the legacy `mcj` key (formerly the
// Material Collection Journal page, retired; `inventory` now exclusively
// governs this module).
function InventoryPage() {
  const { name, role } = useOutletContext();
  const accessLevel = useCatalogueAccess('inventory');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <Inventory accessLevel={accessLevel} userName={name} role={role} />;
}

// Factory Stock Costing — a full page (not a modal), gated to Inventory Full
// Access only (accessLevel 2), stricter than the base table's Limited+Full
// read gate: this page surfaces spend/loss/supplier figures, more sensitive
// than a plain stock count. Falls back to the base Inventory table for
// anyone without Full access, rather than home — it's a deeper view of a
// module they can otherwise legitimately use.
function InventoryCostingPage() {
  const { name } = useOutletContext();
  const accessLevel = useCatalogueAccess('inventory');
  if (accessLevel !== 2) return <Navigate to="/inventory" replace />;
  return <FactoryStockCostingPage userName={name} />;
}

// Per-project inventory-consumption detail (route inventory/projects/:projectId).
// Same read gate as the base Inventory table (Limited+Full).
function InventoryProjectDetailPage() {
  const accessLevel = useCatalogueAccess('inventory');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <InventoryProjectDetail />;
}

// Movement History — the raw inventory ledger (route inventory/movement-history).
// Same read gate as the base Inventory table (Limited+Full), NOT the Full-only
// gate used by Factory Stock Costing: this page carries no spend/loss figures,
// and shows the same movements a Limited user can already read in an item's
// detail modal. That equivalence is the reason the gate can sit here — adding a
// value/cost column to the page would break it and require raising this to 2.
function InventoryMovementHistoryPage() {
  const accessLevel = useCatalogueAccess('inventory');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <MovementHistory />;
}

function NotesPage() {
  const { role, userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('notes');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <Notes role={role} userId={userId} userName={name} />;
}

function AttendancePage() {
  const { role, userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('attendance');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <Attendance role={role} accessLevel={accessLevel} userId={userId} userName={name} />;
}

// ── Works Catalogue ────────────────────────────────────────────────────────
// Three-tier access: 0 hides the route entirely, 1 is read-only (browse and
// present), 2 authors. The level is passed down so the pages can drop their
// authoring affordances — firestore.rules is the actual boundary.
function WorksCataloguePage() {
  const { userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('works_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <WorksCatalogue accessLevel={accessLevel} userId={userId} userName={name} />;
}

// One project's photo stream (v1 photos-first).
function WorksCatalogueProjectPage() {
  const { userId, name } = useOutletContext();
  const accessLevel = useCatalogueAccess('works_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return <WorksCatalogueProject accessLevel={accessLevel} userId={userId} userName={name} />;
}


function ClaimsPage() {
  const { userId, email, phoneNumber, name, role } = useOutletContext();
  const accessLevel = useCatalogueAccess('claims');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return (
    <Claims
      userId={userId}
      email={email}
      phoneNumber={phoneNumber}
      name={name}
      role={role}
      accessLevel={accessLevel}
    />
  );
}

function MaterialCataloguePage() {
  const { role, presentationMode, onTogglePresentationMode } = useOutletContext();
  const accessLevel = useCatalogueAccess('material_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return (
    <MaterialCatalogue
      role={role}
      accessLevel={accessLevel}
      presentationMode={presentationMode}
      onTogglePresentationMode={onTogglePresentationMode}
    />
  );
}

function MaterialCategoryPage() {
  const { role, presentationMode, onTogglePresentationMode } = useOutletContext();
  const accessLevel = useCatalogueAccess('material_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return (
    <MaterialCategory
      role={role}
      accessLevel={accessLevel}
      presentationMode={presentationMode}
      onTogglePresentationMode={onTogglePresentationMode}
    />
  );
}

function MaterialProductPage() {
  const { role, presentationMode, onTogglePresentationMode } = useOutletContext();
  const accessLevel = useCatalogueAccess('material_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return (
    <MaterialProduct
      role={role}
      accessLevel={accessLevel}
      presentationMode={presentationMode}
      onTogglePresentationMode={onTogglePresentationMode}
    />
  );
}

function MaterialProductV2Page() {
  const { role, presentationMode, onTogglePresentationMode } = useOutletContext();
  const accessLevel = useCatalogueAccess('material_catalogue');
  if (accessLevel === 0) return <Navigate to="/" replace />;
  return (
    <MaterialProductV2
      role={role}
      accessLevel={accessLevel}
      presentationMode={presentationMode}
      onTogglePresentationMode={onTogglePresentationMode}
    />
  );
}

function AuthRoot() {
  const auth = useAuth();

  // Wait for the first onAuthStateChanged callback to resolve (fresh login vs.
  // a restored session vs. an expired session) before deciding what to render.
  // Without this gate, a restored/expired session briefly flashes LoginForm
  // before jumping into the app or landing back on it with a reason banner.
  if (!auth.authResolved) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f5f7fa',
        color: '#6b7280',
        fontSize: '16px'
      }}>
        Loading...
      </div>
    );
  }

  const loginScreen = (
    <LoginForm
      logoutReason={auth.logoutReason}
      onLoginAttemptStart={() => auth.setLogoutReason(null)}
      onLoginSuccess={(nameFromLogin, roleFromLogin, identifier) => {
        auth.setName(nameFromLogin);
        auth.setRole(roleFromLogin);
        auth.setEmail(identifier || '');
        auth.setLogoutReason(null);
      }}
    />
  );

  if (!auth.role) {
    return loginScreen;
  }

  // Role set but Firebase has no session: LoginForm's success callback landed
  // after the listener signed the user out (expired session, or no profile
  // doc). The Firestore profile load that sets `status` will never arrive, so
  // showing the loading state here would hang forever — go back to login.
  if (!auth.hasAuthUser) {
    return loginScreen;
  }

  // Status null = still being loaded from Firestore after auth; show a brief loading state
  if (auth.status === null) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f5f7fa',
        color: '#6b7280',
        fontSize: '16px'
      }}>
        Loading...
      </div>
    );
  }

  // Pending users see a blocking screen until Boss Control approves them
  if (auth.status === 'pending') {
    return <AwaitingApproval name={auth.name} onLogout={auth.handleLogout} />;
  }

  // Retired staff are locked out here as well as in Firebase Auth. The Auth
  // account is disabled the moment they're retired, but that only takes effect
  // when the ID token next refreshes (up to an hour), so without this check an
  // already-open session would keep working until then.
  if (auth.status === 'retired') {
    return <AccountRetired name={auth.name} onLogout={auth.handleLogout} />;
  }

  return <AuthenticatedChrome />;
}

function AuthenticatedChrome() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const prevRoleRef = useRef(auth.role);
  const [presentationMode, setPresentationMode] = useState(false);

  const {
    role,
    name,
    email,
    userId,
    phoneNumber,
    catalogueAccessLevels,
    showProfile,
    setShowProfile,
    isPanelOpen,
    setIsPanelOpen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    handleLogout,
    setName,
    setEmail: setEmailState
  } = auth;

  const handleTogglePresentationMode = useCallback(() => {
    setPresentationMode((prev) => !prev);
  }, []);

  useEffect(() => {
    if (
      presentationMode &&
      !location.pathname.startsWith('/works-catalogue') &&
      !location.pathname.startsWith('/material-catalogue')
    ) {
      setPresentationMode(false);
    }
  }, [location.pathname, presentationMode]);

  useEffect(() => {
    const prevRole = prevRoleRef.current;
    if (!prevRole && role) {
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    }
    prevRoleRef.current = role;
  }, [role, location.pathname, navigate]);

  const formatRole = (r) => {
    if (!r) return '';
    if (r === 'bosscontrol') return 'Boss Control';
    return r.charAt(0).toUpperCase() + r.slice(1);
  };

  const displayName = role === 'worker' ? name : `${name} (${formatRole(role)})`;

  const outletContext = useMemo(
    () => ({
      role,
      userId,
      email,
      phoneNumber,
      name,
      catalogueAccessLevels,
      presentationMode,
      onTogglePresentationMode: handleTogglePresentationMode
    }),
    [role, userId, email, phoneNumber, name, catalogueAccessLevels, presentationMode, handleTogglePresentationMode]
  );

  return (
    <GSTDisplayProvider>
    <UnsavedChangesProvider>
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: '#f5f7fa',
        position: 'relative'
      }}
    >
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <Sidebar
        role={role}
        catalogueAccessLevels={catalogueAccessLevels}
        isCollapsed={isSidebarCollapsed}
        isMobileOpen={isMobileMenuOpen}
        presentationMode={presentationMode}
        onToggle={() => {
          setIsSidebarCollapsed(prev => !prev);
        }}
        onMobileToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />

      <div
        className="main-content-wrapper"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          background: '#f5f7fa',
          height: '100vh',
          position: 'relative'
        }}
      >
        <Header
          userName={displayName}
          role={role}
          isPanelOpen={isPanelOpen}
          togglePanel={() => setIsPanelOpen((prev) => !prev)}
          onLogout={handleLogout}
          onProfileClick={() => {
            setShowProfile(true);
            setIsPanelOpen(false);
          }}
          onMobileMenuToggle={() => setIsMobileMenuOpen((prev) => !prev)}
        />
        <main
          style={{
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
            background: '#f5f7fa',
            position: 'relative'
          }}
        >
          {showProfile ? (
            <Profile
              email={email}
              onClose={() => setShowProfile(false)}
              onProfileUpdate={(newName, newEmail) => {
                setName(newName);
                setEmailState(newEmail);
              }}
            />
          ) : (
            // Single boundary for every lazy route. It sits inside <main>, so the
            // sidebar and header stay mounted and only the content area swaps.
            <Suspense fallback={<RouteFallback />}>
              <Outlet context={outletContext} />
            </Suspense>
          )}
        </main>
      </div>
    </div>
    </UnsavedChangesProvider>
    </GSTDisplayProvider>
  );
}

const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <AuthRoot />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'dashboard', element: <RoleRoute allow={['bosscontrol', 'accountant']} component={Dashboard} /> },
      { path: 'projects', element: <GatedRoleRoute feature="projects" component={ProjectsList} /> },
      { path: 'projects/all/:status', element: <GatedRoleRoute feature="projects" component={AllProjectsView} /> },
      { path: 'team-management', element: <GatedRoleRoute feature="team_management" component={TeamManagement} /> },
      { path: 'sop', element: <SOPPage /> },
      { path: 'sop/new', element: <SOPDetailRoute /> },
      { path: 'sop/:id', element: <SOPDetailRoute /> },
      { path: 'sales-invoices', element: <GatedRoleRoute feature="sales_invoices" allow={SALES_ROLES} component={SalesInvoicesList} /> },
      // `analytics` is a static segment, so it ranks above the `:projectId`
      // route below and there is no clash — same reasoning as the `v2` segment
      // in the material-catalogue routes further down.
      { path: 'sales-invoices/analytics', element: <GatedRoleRoute feature="sales_invoices" allow={SALES_ROLES} component={SalesAnalyticsPage} /> },
      { path: 'sales-invoices/:projectId', element: <GatedRoleRoute feature="sales_invoices" allow={SALES_ROLES} component={SalesInvoiceProject} /> },
      {
        path: 'sales-invoices/:projectId/quotation/:quotationId',
        element: <GatedRoleRoute feature="sales_invoices" allow={SALES_ROLES} component={QuotationEditorPage} />
      },
      { path: 'project-costing',                element: <ProjectCostingPage /> },
      { path: 'project-costing/master-list',    element: <ProjectCostingPage /> },
      { path: 'project-costing/:projectId',     element: <ProjectCostingDetailPage /> },
      // DEPRECATED: /quotation routes — redirected to /sales-invoices (Quotation & Invoices).
      // QuotationList, QuotationQuickView, CreateQuotation components kept for future reuse.
      { path: 'quotation', element: <Navigate to="/sales-invoices" replace /> },
      { path: 'quotation/:projectId', element: <Navigate to="/sales-invoices" replace /> },
      { path: 'quotation/:projectId/edit/:quoteId', element: <Navigate to="/sales-invoices" replace /> },
      { path: 'claims', element: <ClaimsPage /> },
      { path: 'general-expenses', element: <GeneralExpensesPage /> },
      { path: 'daily-transport', element: <DailyTransportPage /> },
      { path: 'material-catalogue', element: <MaterialCataloguePage /> },
      // The `v2` segment is static, so these rank above the `:productId` routes
      // below and there is no clash between "v2" and a product id.
      {
        path: 'material-catalogue/v2/category/:categoryId/product/:productId',
        element: <MaterialProductV2Page />
      },
      { path: 'material-catalogue/v2/product/:productId', element: <MaterialProductV2Page /> },
      { path: 'material-catalogue/category/:categoryId', element: <MaterialCategoryPage /> },
      {
        path: 'material-catalogue/category/:categoryId/product/:productId',
        element: <MaterialProductPage />
      },
      { path: 'material-catalogue/product/:productId', element: <MaterialProductPage /> },
      { path: 'works-catalogue', element: <WorksCataloguePage /> },
      { path: 'works-catalogue/project/:projectId', element: <WorksCatalogueProjectPage /> },
      // Item/detail pages retired with the photos-first rebuild; old links land on the home.
      { path: 'works-catalogue/item/:itemId', element: <Navigate to="/works-catalogue" replace /> },
      { path: 'works-catalogue/detail/:detailId', element: <Navigate to="/works-catalogue" replace /> },
      { path: 'system-furniture-catalogue', element: <SystemFurnitureCatalogue /> },
      { path: 'supplier-list', element: <GatedRoleRoute feature="supplier_list" allow={SUPPLIER_ROLES} component={SupplierList} /> },
      { path: 'supplier-list/contacts/:supplierId', element: <GatedRoleRoute feature="supplier_list" allow={SUPPLIER_ROLES} component={SupplierContacts} /> },
      { path: 'defect-tracker', element: <DefectTrackerPage /> },
      { path: 'purchasing', element: <PurchasingPage /> },
      { path: 'inventory', element: <InventoryPage /> },
      { path: 'inventory/costing', element: <InventoryCostingPage /> },
      { path: 'inventory/movement-history', element: <InventoryMovementHistoryPage /> },
      { path: 'inventory/projects/:projectId', element: <InventoryProjectDetailPage /> },
      // Follow Ups logistics hub (new feature under Core Navigation)
      { path: 'follow-ups', element: <FollowUpsNewPage /> },
      { path: 'follow-ups/list/:listId', element: <FollowUpsListDetailPage /> },
      { path: 'follow-ups/:projectId', element: <FollowUpsProjectDetailPage /> },
      // Legacy route for old FollowUps — redirect to new path
      { path: 'follow-ups-legacy', element: <FollowUpsPage /> },
      // Notes — personal PM knowledge capture
      { path: 'notes', element: <NotesPage /> },
      { path: 'attendance', element: <AttendancePage /> },
      { path: 'access-permissions', element: <FeatureRoute feature="access_permissions" minLevel={2} component={AccessPermissions} /> },
      // Knowledge Base — Carpentry Details
      { path: '*', element: <Navigate to="/" replace /> }
    ]
  }
]);

function App() {
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null); // null = loading, 'pending' | 'active'
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [catalogueAccessLevels, setCatalogueAccessLevels] = useState({
    material_catalogue: 2,
    works_catalogue: 2,
    follow_ups: 2,
    inventory: 2,
    attendance: 0,
    notes: 2,
  });
  const [showProfile, setShowProfile] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // authResolved gates the initial render: stays false until the first
  // onAuthStateChanged callback finishes, so a restored/expired session is
  // never shown a flash of LoginForm before it resolves.
  const [authResolved, setAuthResolved] = useState(false);
  // Whether Firebase currently has a signed-in user. Tracked separately from
  // `role` because LoginForm sets `role` from its own flow — if that flow loses
  // a race with a sign-out (expired session, missing profile doc), role would
  // be set with no session behind it and the app would hang on "Loading...".
  const [hasAuthUser, setHasAuthUser] = useState(false);
  // Set when a session is force-ended by the idle/absolute timeout, so
  // LoginForm can explain why the user landed back there.
  const [logoutReason, setLogoutReason] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setHasAuthUser(true);

          // A fresh interactive login owns the timers outright — it must never
          // be judged against the previous session's leftover timestamps.
          // sessionStart/lastActivity live in localStorage and survive a closed
          // browser, while auth uses browserSessionPersistence and does not, so
          // without this branch the first login after a >60min gap was expired
          // on arrival and signed straight back out. See markLoginAttempt().
          if (consumeLoginAttempt()) {
            startSession();
          } else {
            // Restored session — enforce idle (60min) + absolute (8h) timeouts.
            // See src/utils/sessionTimeout.js for the storage rationale.
            ensureSessionStart();
            const expiryReason = checkSessionExpiry();
            if (expiryReason) {
              clearSession();
              setLogoutReason(expiryReason);
              await auth.signOut();
              return;
            }
          }
          touchActivity();

          setUserId(user.uid);

          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              const userName = userData.name || 'User';
              const userPhone = userData.phoneNumber || '';
              const userEmail = userData.email || user.email || '';
              // Default to 'active' for existing users who pre-date the status field
              const userStatus = userData.status || 'active';

              const userRole = await getUserRole();

              const customPerms = userData.customPermissions;
              const moneyPerms = userData.moneyPermissions;
              setCatalogueAccessLevels(
                buildCatalogueAccessLevels(customPerms, moneyPerms, userRole),
              );

              setName(userName);
              setRole(userRole);
              setStatus(userStatus);
              setEmail(userEmail);
              setPhoneNumber(userPhone);

              try {
                sessionStorage.setItem(
                  'user',
                  JSON.stringify({
                    uid: user.uid,
                    phoneNumber: userPhone,
                    email: userEmail,
                    name: userName
                  })
                );
              } catch (storageErr) {
                console.warn('Unable to update sessionStorage:', storageErr);
              }
            } else {
              // No Firestore doc — user was deleted; sign them out
              console.warn('No Firestore user document found — signing out');
              try { sessionStorage.removeItem('user'); } catch (e) { /* ignore */ }
              clearSession();
              await auth.signOut();
              return;
            }
          } catch (error) {
            console.error('Error loading user profile:', error);
            const savedUser = sessionStorage.getItem('user');
            if (savedUser) {
              try {
                const userData = JSON.parse(savedUser);
                const userRole = await getUserRole();
                setName(userData.name || 'User');
                setRole(userRole);
                setStatus('active'); // On error, do not block access
                setEmail(userData.email || userData.phoneNumber || '');
                // The whole map, not the three modules this used to build:
                // useCatalogueAccess now fails closed, so a partial map would
                // lock the user out of everything else after a transient
                // profile-read failure. Role-only derivation (no stored perms)
                // is the safest thing available on this path.
                setCatalogueAccessLevels(
                  buildCatalogueAccessLevels(undefined, undefined, userRole),
                );
              } catch (err) {
                console.error('Error loading from sessionStorage:', err);
              }
            }
          }
        } else {
          setHasAuthUser(false);
          clearLoginAttempt();
          setRole(null);
          setStatus(null);
          setName('');
          setUserId('');
          setEmail('');
          setPhoneNumber('');
          setCatalogueAccessLevels({ material_catalogue: 2, works_catalogue: 2 });
        }
      } finally {
        setAuthResolved(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }

    setRole(null);
    setStatus(null);
    setName('');
    setUserId('');
    setEmail('');
    setPhoneNumber('');
    setCatalogueAccessLevels({ material_catalogue: 2, works_catalogue: 2 });
    setShowProfile(false);
    setIsPanelOpen(false);
    setIsSidebarCollapsed(false);
    sessionStorage.removeItem('user');
    clearSession();
  }, []);

  // Live idle/absolute session tracking while the app is open — handles the
  // case where a tab stays open past the timeout, on top of the load/resume
  // check inside onAuthStateChanged above.
  const handleSessionExpire = useCallback((reason) => {
    setLogoutReason(reason);
    handleLogout();
  }, [handleLogout]);
  useSessionTimeout(Boolean(role), handleSessionExpire);

  const authValue = useMemo(
    () => ({
      role,
      setRole,
      status,
      setStatus,
      name,
      setName,
      email,
      setEmail,
      userId,
      phoneNumber,
      catalogueAccessLevels,
      showProfile,
      setShowProfile,
      isPanelOpen,
      setIsPanelOpen,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      isMobileMenuOpen,
      setIsMobileMenuOpen,
      handleLogout,
      authResolved,
      hasAuthUser,
      logoutReason,
      setLogoutReason
    }),
    [
      role,
      status,
      name,
      email,
      userId,
      phoneNumber,
      catalogueAccessLevels,
      showProfile,
      isPanelOpen,
      isSidebarCollapsed,
      isMobileMenuOpen,
      handleLogout,
      authResolved,
      hasAuthUser,
      logoutReason
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <VersionChecker />
      <RouterProvider router={appRouter} />
    </AuthContext.Provider>
  );
}

export default App;
